<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="rto-page">
    <div class="page-header">
      <div class="page-header-text">
        <div class="page-eyebrow">Flota</div>
        <h1 class="page-title">Najem z wykupem</h1>
      </div>
      <button class="action-pill action-pill--primary" @click="openCreate">
        <v-icon size="14">mdi-plus</v-icon>Nowa umowa
      </button>
    </div>

    <!-- LISTA UMÓW -->
    <v-card class="rto-card mb-4" :loading="loading">
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="contracts"
          :loading="loading"
          dark
          class="rto-table"
          no-data-text="Nie ma jeszcze żadnej umowy z wykupem"
        >
          <template v-slot:item.driver="{ item }">
            {{ item.driver ? item.driver.firstName + ' ' + item.driver.lastName : '—' }}
          </template>
          <template v-slot:item.car="{ item }">
            <span class="mono">{{ item.car ? item.car.plate : '—' }}</span>
            <div class="hint">{{ item.car ? item.car.make + ' ' + item.car.model : '' }}</div>
          </template>
          <template v-slot:item.weekly="{ item }">
            <strong>{{ zl(item.weeklyTotalCents) }}</strong>
            <div class="hint">
              najem {{ zl(item.weeklyBaseRentCents) }} · wykup {{ zl(item.weeklyBuyoutCents) }} ·
              ubezp. {{ zl(item.weeklyInsuranceCents) }}
            </div>
          </template>
          <template v-slot:item.progress="{ item }">
            <v-progress-linear
              :value="progressPct(item)"
              height="8"
              rounded
              color="#FFC107"
              background-color="#2A3042"
            />
            <div class="hint">
              {{ zl(item.collectedBuyoutCents) }} z {{ zl(item.buyoutTargetCents) }}
            </div>
          </template>
          <template v-slot:item.status="{ item }">
            <span class="chip" :class="'chip--' + item.status.toLowerCase()">
              {{ statusLabel(item.status) }}
            </span>
            <div v-if="item.status === 'ACTIVE' && item.missedWeeksAllowed" class="hint">
              zapas: {{ item.missedWeeksAllowed - item.missedWeeksCount }} tydz.
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn small text color="#5478FF" @click="openDetails(item)">Harmonogram</v-btn>
            <v-btn
              v-if="item.status === 'ACTIVE'"
              small
              text
              color="#FF5252"
              @click="askTerminate(item)"
            >Zerwij</v-btn>
            <v-btn
              v-if="item.status === 'TERMINATED'"
              small
              text
              color="#4CAF50"
              @click="resume(item)"
            >Wznów</v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- NOWA UMOWA -->
    <v-dialog v-model="createDialog" max-width="720" persistent>
      <v-card class="rto-dialog" dark>
        <v-card-title>Nowa umowa z wykupem</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.driverId"
                :items="drivers"
                item-text="name"
                item-value="id"
                label="Kierowca"
                outlined dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.carId"
                :items="cars"
                item-text="label"
                item-value="id"
                label="Auto"
                outlined dense
              />
            </v-col>
          </v-row>

          <div class="section-title">Harmonogram</div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.startDate"
                label="Początek (przesuniemy na poniedziałek)"
                type="date"
                outlined dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.weeks"
                label="Liczba rat tygodniowych"
                type="number" min="1"
                outlined dense
              />
            </v-col>
          </v-row>

          <div class="section-title">Czynsz tygodniowy — składniki</div>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form.baseRent"
                label="Najem (zł)" type="number" outlined dense
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form.buyout"
                label="Rata wykupu (zł)" type="number" outlined dense
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form.insurance"
                label="Ubezpieczenie (zł)" type="number" outlined dense
              />
            </v-col>
          </v-row>
          <div class="sum-box">
            Czynsz tygodniowy: <strong>{{ sumZl }} zł</strong>
            <span class="hint"> · cena wykupu: {{ buyoutTargetZl }} zł przez {{ form.weeks || 0 }} tyg.</span>
          </div>

          <div class="section-title">Zasady</div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.missedWeeksAllowed"
                label="Ile tygodni z niedoborem wolno przepuścić"
                hint="0 = pierwszy niewyrobiony tydzień zrywa umowę"
                persistent-hint type="number" min="0" outlined dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.tolerance"
                label="Tolerancja niedoboru (zł)"
                hint="Brak poniżej tej kwoty nie liczy się jako niewyrobiony tydzień"
                persistent-hint type="number" min="0" outlined dense
              />
            </v-col>
          </v-row>
          <v-textarea v-model="form.note" label="Notatka (opcjonalnie)" rows="2" outlined dense class="mt-3" />

          <v-alert dense text type="info" class="mt-2">
            Po zerwaniu umowy czynsz wraca do stawki z karty auta, a odłożone raty
            zostają zamrożone — wrócą, jeśli wznowisz umowę.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="createDialog = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="saving" @click="create">Załóż umowę</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- HARMONOGRAM -->
    <v-dialog v-model="detailsDialog" max-width="820">
      <v-card class="rto-dialog" dark v-if="details">
        <v-card-title>
          Harmonogram — {{ details.car ? details.car.plate : '' }}
        </v-card-title>
        <v-card-text>
          <div class="hint mb-3">
            Odłożone {{ zl(details.collectedBuyoutCents) }} z {{ zl(details.buyoutTargetCents) }}
            <span v-if="details.status === 'TERMINATED'"> · saldo zamrożone</span>
          </div>
          <v-simple-table dark dense>
            <thead>
              <tr>
                <th>#</th><th>Tydzień</th><th>Czynsz</th><th>Pokryto</th><th>Zabrakło</th><th>Stan</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="i in details.installments" :key="i.id">
                <td>{{ i.index }}</td>
                <td class="mono">{{ shortDate(i.weekStart) }}</td>
                <td>{{ zl(i.plannedTotalCents) }}</td>
                <td>{{ i.status === 'PENDING' ? '—' : zl(i.coveredCents) }}</td>
                <td :class="{ 'text-danger': i.shortfallCents > 0 }">
                  {{ i.shortfallCents > 0 ? zl(i.shortfallCents) : '—' }}
                </td>
                <td>
                  <span class="chip" :class="'chip--' + i.status.toLowerCase()">
                    {{ instLabel(i.status) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="detailsDialog = false">Zamknij</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const axios = require('axios');
import Swal from 'sweetalert2';

export default {
  name: 'RentToOwnPage',
  data() {
    return {
      loading: false,
      saving: false,
      contracts: [],
      drivers: [],
      cars: [],
      createDialog: false,
      detailsDialog: false,
      details: null,
      form: this.emptyForm(),
      headers: [
        { text: 'Kierowca', value: 'driver' },
        { text: 'Auto', value: 'car' },
        { text: 'Czynsz tygodniowy', value: 'weekly' },
        { text: 'Postęp wykupu', value: 'progress' },
        { text: 'Stan', value: 'status' },
        { text: '', value: 'actions', sortable: false },
      ],
    };
  },
  computed: {
    sumZl() {
      const s = (this.form.baseRent || 0) + (this.form.buyout || 0) + (this.form.insurance || 0);
      return s.toFixed(2);
    },
    buyoutTargetZl() {
      return ((this.form.buyout || 0) * (this.form.weeks || 0)).toFixed(2);
    },
  },
  async mounted() {
    await this.load();
    await this.loadSelects();
  },
  methods: {
    emptyForm() {
      return {
        driverId: null, carId: null, startDate: '', weeks: 52,
        baseRent: 0, buyout: 0, insurance: 0,
        missedWeeksAllowed: 0, tolerance: 0, note: '',
      };
    },
    api(p) { return process.env.VUE_APP_ROOT_API + p; },
    auth() { return { Authorization: 'Bearer ' + localStorage.getItem('token') }; },
    zl(cents) { return ((cents || 0) / 100).toFixed(2) + ' zł'; },
    shortDate(d) { return d ? new Date(d).toLocaleDateString('pl-PL') : '—'; },
    progressPct(c) {
      if (!c.buyoutTargetCents) return 0;
      return Math.min(100, (c.collectedBuyoutCents / c.buyoutTargetCents) * 100);
    },
    statusLabel(s) {
      return { ACTIVE: 'Aktywna', TERMINATED: 'Zerwana', COMPLETED: 'Wykupione' }[s] || s;
    },
    instLabel(s) {
      return { PENDING: 'czeka', PAID: 'wyrobiony', PARTIAL: 'niewyrobiony', SKIPPED: 'pominięty' }[s] || s;
    },
    async load() {
      this.loading = true;
      try {
        const res = await axios({ method: 'get', url: this.api('rent-to-own'), headers: this.auth() });
        this.contracts = res.data.data || [];
      } catch (e) {
        Swal.fire('Błąd', 'Nie udało się pobrać umów', 'error');
      }
      this.loading = false;
    },
    async loadSelects() {
      try {
        const d = await axios({ method: 'get', url: this.api('drivers?take=200&skip=1'), headers: this.auth() });
        this.drivers = (d.data.data.result || []).map((it) => ({
          id: it.id, name: `${it.firstName} ${it.lastName}`,
        }));
        const c = await axios({ method: 'get', url: this.api('cars?take=200&skip=1'), headers: this.auth() });
        const list = c.data.data.result || c.data.data || [];
        this.cars = list.map((it) => ({
          id: it.id, label: `${it.plate} — ${it.make} ${it.model}`,
        }));
      } catch (e) {
        console.log(e);
      }
    },
    openCreate() {
      this.form = this.emptyForm();
      this.createDialog = true;
    },
    async create() {
      const total = Math.round(
        ((this.form.baseRent || 0) + (this.form.buyout || 0) + (this.form.insurance || 0)) * 100,
      );
      if (!this.form.driverId || !this.form.carId || !this.form.startDate) {
        Swal.fire('Brakuje danych', 'Wybierz kierowcę, auto i datę startu', 'warning');
        return;
      }
      if (!this.form.buyout) {
        Swal.fire('Brakuje raty wykupu', 'Bez raty wykupu to zwykły wynajem', 'warning');
        return;
      }
      this.saving = true;
      try {
        await axios({
          method: 'post',
          url: this.api('rent-to-own'),
          headers: this.auth(),
          data: {
            driverId: this.form.driverId,
            carId: this.form.carId,
            startDate: this.form.startDate,
            weeks: this.form.weeks,
            weeklyTotalCents: total,
            weeklyBaseRentCents: Math.round((this.form.baseRent || 0) * 100),
            weeklyBuyoutCents: Math.round((this.form.buyout || 0) * 100),
            weeklyInsuranceCents: Math.round((this.form.insurance || 0) * 100),
            missedWeeksAllowed: this.form.missedWeeksAllowed || 0,
            shortfallToleranceCents: Math.round((this.form.tolerance || 0) * 100),
            note: this.form.note || undefined,
          },
        });
        this.createDialog = false;
        await this.load();
        Swal.fire('Gotowe', 'Umowa założona', 'success');
      } catch (e) {
        Swal.fire('Błąd', (e.response && e.response.data && e.response.data.message) || 'Nie udało się', 'error');
      }
      this.saving = false;
    },
    async openDetails(item) {
      try {
        const res = await axios({ method: 'get', url: this.api('rent-to-own/' + item.id), headers: this.auth() });
        this.details = res.data.data;
        this.detailsDialog = true;
      } catch (e) {
        Swal.fire('Błąd', 'Nie udało się pobrać harmonogramu', 'error');
      }
    },
    async askTerminate(item) {
      const { value: reason, isConfirmed } = await Swal.fire({
        title: 'Zerwać umowę?',
        text: 'Czynsz wróci do stawki z karty auta, a odłożone raty zostaną zamrożone. Kierowca dostanie SMS.',
        input: 'text',
        inputPlaceholder: 'Powód (trafi do historii umowy)',
        showCancelButton: true,
        confirmButtonText: 'Zerwij',
        cancelButtonText: 'Anuluj',
      });
      if (!isConfirmed) return;
      try {
        await axios({
          method: 'post',
          url: this.api('rent-to-own/' + item.id + '/terminate'),
          headers: this.auth(),
          data: { reason: reason || 'Decyzja biura' },
        });
        await this.load();
      } catch (e) {
        Swal.fire('Błąd', 'Nie udało się zerwać umowy', 'error');
      }
    },
    async resume(item) {
      const ok = await Swal.fire({
        title: 'Wznowić umowę?',
        text: `Zamrożone ${this.zl(item.collectedBuyoutCents)} wróci na licznik wykupu, a kierowca dostanie SMS.`,
        showCancelButton: true,
        confirmButtonText: 'Wznów',
        cancelButtonText: 'Anuluj',
      });
      if (!ok.isConfirmed) return;
      try {
        await axios({
          method: 'post',
          url: this.api('rent-to-own/' + item.id + '/resume'),
          headers: this.auth(),
        });
        await this.load();
      } catch (e) {
        Swal.fire('Błąd', 'Nie udało się wznowić umowy', 'error');
      }
    },
  },
};
</script>

<style scoped>
.rto-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.page-header { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; padding: 4px 4px 18px; }
.page-eyebrow { font-size: 12px; color: #6E7691; font-weight: 500; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }
.action-pill { display: inline-flex; align-items: center; gap: 6px; background: #2962FF; border: 1px solid #2962FF; color: #fff; padding: 8px 14px; border-radius: 10px; font-size: 12.5px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 12px rgba(41,98,255,0.3); font-family: inherit; }
.rto-card, .rto-dialog { background: #232838 !important; border: 1px solid #2A3042; border-radius: 14px; }
.rto-table { background: transparent !important; }
.hint { font-size: 11.5px; color: #8B93AD; margin-top: 2px; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.section-title { font-size: 12px; font-weight: 700; color: #8B93AD; text-transform: uppercase; letter-spacing: 0.04em; margin: 14px 0 6px; }
.sum-box { background: #1C2130; border: 1px solid #2A3042; border-radius: 10px; padding: 10px 12px; font-size: 13px; }
.chip { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 11px; font-weight: 700; }
.chip--active { background: rgba(76,175,80,0.15); color: #4CAF50; }
.chip--terminated { background: rgba(255,82,82,0.15); color: #FF5252; }
.chip--completed { background: rgba(255,193,7,0.15); color: #FFC107; }
.chip--paid { background: rgba(76,175,80,0.15); color: #4CAF50; }
.chip--partial { background: rgba(255,152,0,0.15); color: #FF9800; }
.chip--pending { background: rgba(139,147,173,0.15); color: #8B93AD; }
.chip--skipped { background: rgba(139,147,173,0.1); color: #6E7691; }
.text-danger { color: #FF5252; }
</style>
